/**
 * @fileoverview Student "My tasks" page (AR-543, Arc-only). Lists the student's
 * tasks (deliverable / self-assessment / final) with a project filter and opens
 * the matching submission drawer. Wrapped in the app providers + Layout.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Container, Stack } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Layout } from 'components/Layout'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { notifications, NotificationProvider, useCurrentUser } from 'context'
import { TaskTrackerAssessmentDrawer } from 'pages/TaskTracker/assessmentDrawer'
import {
  StudentTasksEmptyState,
  StudentTasksFilters,
  StudentTasksHeader,
  StudentTasksList,
  StudentTasksListSkeleton,
  type EmptyVariant,
} from './components'
import { ALL_PROJECTS } from './constants'
import { invalidateTasksSpine } from './service/realSource'
import { useStudentTasks } from './hooks/useStudentTasks'
import { StudentDeliverableDrawer } from './deliverableDrawer'
import { StudentFinalAssessmentDrawer } from './finalAssessmentDrawer'
import type { StudentTask } from './types'

function StudentTasksView() {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const studentId = currentUser?.profile_url?.split('/')[2] ?? currentUser?.id ?? ''
  const { tasks, projectOptions, loading, error, selectedProjectId, setProjectId, markDone } =
    useStudentTasks()
  const [active, setActive] = useState<StudentTask | null>(null)

  useEffect(() => {
    if (!error) return
    notifications.show({
      message: t('student_tasks.error.load_failed'),
      color: 'red',
      icon: <IconX size={18} />,
    })
  }, [error, t])

  const emptyVariant = useMemo<EmptyVariant | null>(() => {
    if (loading || tasks.length > 0) return null
    if (error) return 'error'
    return selectedProjectId === ALL_PROJECTS ? 'no_tasks' : 'no_results'
  }, [loading, tasks.length, error, selectedProjectId])

  const closeDrawer = useCallback(() => setActive(null), [])
  const handleSaved = useCallback(() => {
    setActive(current => {
      if (current) markDone(current.id)
      return current
    })
    // The self-assessment drawer saves outside the source (scoreUpdateService),
    // so refresh the spine here too; double invalidation is deduped by loadCached.
    invalidateTasksSpine()
  }, [markDone])

  return (
    <Layout>
      <Container size="xl" py="xl">
        <Stack spacing="lg">
          <StudentTasksHeader />

          {!loading && tasks.length > 0 && (
            <StudentTasksFilters
              projects={projectOptions}
              value={selectedProjectId}
              onChange={setProjectId}
            />
          )}

          {loading ? (
            <StudentTasksListSkeleton />
          ) : emptyVariant ? (
            <StudentTasksEmptyState variant={emptyVariant} />
          ) : (
            <StudentTasksList tasks={tasks} onOpen={setActive} />
          )}
        </Stack>
      </Container>

      <StudentDeliverableDrawer
        opened={active?.type === 'deliverable'}
        identifierId={active?.identifierId ?? ''}
        onClose={closeDrawer}
        onSubmitted={handleSaved}
      />

      <TaskTrackerAssessmentDrawer
        opened={active?.type === 'self-assessment'}
        sessionMeetingId={active?.type === 'self-assessment' ? active.identifierId : ''}
        studentId={studentId}
        assessmentId={null}
        lessonTitle={active?.lessonTitle ?? undefined}
        lessonDate={active?.lessonDate ?? undefined}
        onClose={closeDrawer}
        onSaved={handleSaved}
      />

      <StudentFinalAssessmentDrawer
        opened={active?.type === 'final-self-assessment'}
        identifierId={active?.identifierId ?? ''}
        onClose={closeDrawer}
        onSaved={handleSaved}
      />
    </Layout>
  )
}

export function StudentTasksPage() {
  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <StudentTasksView />
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
